import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, ArrowRight, Sparkles } from 'lucide-react';

const menuItems = [
  { label: 'Forside', href: '/' },
  { label: 'Profil', href: '/profil' },
  {
    label: 'Ydelser',
    href: '#',
    children: [
      { label: 'Maling af vægge', href: '/ydelser/maling-af-vaegge' },
      { label: 'Spartling af vægge', href: '/ydelser/spartling-af-vaegge' },
      { label: 'Opsætning af filt', href: '/ydelser/opsaetning-af-filt' },
      { label: 'Opsætning af tapet', href: '/ydelser/tapet' },
      { label: 'Erhvervs- og nybyggeri', href: '/ydelser/erhvervs-og-nybyggeri' },
      { label: 'Vedligeholdelsesarbejde', href: '/ydelser/vedligeholdelsesarbejde' },
      { label: 'Udvendigt malerarbejde', href: '/ydelser/udvendigt-malerarbejde' },
    ],
  },
  { label: 'Galleri', href: '/galleri' },
  { label: 'Kundecases', href: '/kundecases' },
  { label: 'Lærlinge', href: '/laerling-i-bm-maler' },
  { label: 'Ledige stillinger', href: '/super-svende-soeges-til-bm-maler-i-vejle' },
  { label: 'Priser', href: '/hvad-koster-en-maler' },
  { label: 'Kontakt', href: '/kontakt' },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  // Track scroll position to apply glassmorphism
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [currentPath]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const isActive = (path) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm' 
        : 'bg-white border-b border-slate-100'
    }`}>
      {/* Top Bar */}
      <div className="bg-slate-900/80 text-white py-2 px-4 sm:px-6 lg:px-8 text-xs sm:text-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-6">
            <a href="tel:+4575695900" aria-label="Ring til BM Maler på 75 69 59 00" className="flex items-center gap-2 text-[#F1B528] hover:text-[#D99C1B] transition-colors whitespace-nowrap">
              <Phone className="w-4 h-4 text-[#F1B528] shrink-0" />
              <span className="font-medium">75 69 59 00</span>
            </a>
            <a href="mailto:bm@bmmaler.dk" aria-label="Send e-mail til bm@bmmaler.dk" className="flex items-center gap-2 text-[#F1B528] hover:text-[#D99C1B] transition-colors whitespace-nowrap">
              <Mail className="w-4 h-4 text-[#F1B528] shrink-0" />
              <span className="font-medium">bm@bmmaler.dk</span>
            </a>
          </div>
          <div className="flex items-center gap-4 text-slate-300 text-xs">
            <span className="flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5 text-[#F1B528]" />
              Uddannede malere med knap 70 års erfaring
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" aria-label="BM Maler A/S Forside">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=85,format=auto/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/97f3f97c-e329-43a2-8e51-2f2cfda5a57e.png"
              alt="BM Maler Logo"
              width="240"
              height="48"
              fetchPriority="high"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav - Hidden to always use burger menu */}
          <div className="hidden"></div>

          {/* Burger Menu Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative w-12 h-12 flex flex-col items-center justify-center rounded-full text-slate-800 hover:text-[#F1B528] hover:bg-slate-100 focus:outline-none transition-colors border border-slate-200 bg-slate-50 group"
              aria-expanded={isOpen}
              aria-label={isOpen ? "Luk menu" : "Åbn menu"}
            >
              <span className="sr-only">Åbn menu</span>
              <div className="w-6 h-4 flex flex-col justify-between relative">
                <span className={`w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
                <span className={`w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
                <span className={`w-full h-0.5 bg-current rounded-full transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen / Overlay Menu Panel */}
      <div className={`fixed inset-0 w-full h-screen z-50 bg-slate-950 overflow-y-auto transition-all duration-500 ease-in-out ${
        isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'
      }`}>
        <div className="min-h-screen flex flex-col bg-slate-950">
          {/* Top Bar inside menu */}
          <div className="bg-slate-900/80 text-white py-2 px-4 sm:px-6 lg:px-8 text-xs sm:text-sm border-b border-white/5">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
              <div className="flex items-center gap-6">
                <a href="tel:+4575695900" className="flex items-center gap-2 text-[#F1B528] hover:text-[#D99C1B] transition-colors whitespace-nowrap">
                  <Phone className="w-4 h-4 text-[#F1B528] shrink-0" />
                  <span className="font-medium">75 69 59 00</span>
                </a>
                <a href="mailto:bm@bmmaler.dk" className="flex items-center gap-2 text-[#F1B528] hover:text-[#D99C1B] transition-colors whitespace-nowrap">
                  <Mail className="w-4 h-4 text-[#F1B528] shrink-0" />
                  <span className="font-medium">bm@bmmaler.dk</span>
                </a>
              </div>
              <div className="flex items-center gap-4 text-slate-300 text-xs">
                <span className="flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-[#F1B528]" />
                  Uddannede malere med knap 70 års erfaring
                </span>
              </div>
            </div>
          </div>

          {/* Header Row inside menu */}
          <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center shrink-0" aria-label="BM Maler A/S Forside">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=85,format=auto/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/97f3f97c-e329-43a2-8e51-2f2cfda5a57e.png"
                  alt="BM Maler Logo"
                  width="240"
                  height="48"
                  className="h-12 w-auto object-contain brightness-0 invert"
                />
              </Link>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsOpen(false)}
                  className="relative w-12 h-12 flex flex-col items-center justify-center rounded-full text-slate-300 hover:text-[#F1B528] hover:bg-white/5 focus:outline-none transition-colors border border-white/10 bg-slate-900/50 group"
                  aria-label="Luk menu"
                >
                  <span className="sr-only">Luk menu</span>
                  <div className="w-6 h-4 flex flex-col justify-between relative">
                    <span className="w-full h-0.5 bg-current rounded-full transition-all duration-300 rotate-45 translate-y-[7px]"></span>
                    <span className="w-full h-0.5 bg-current rounded-full transition-all duration-300 opacity-0"></span>
                    <span className="w-full h-0.5 bg-current rounded-full transition-all duration-300 -rotate-45 -translate-y-[7px]"></span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Menu Content */}
          <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-8 sm:py-12 grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Main Menu Links */}
            <div className="md:col-span-7 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-[#D99C1B] mb-4">Navigation</p>
              {menuItems.map((item, index) => {
                if (item.children) {
                  return (
                    <div key={index} className="space-y-1">
                      <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="flex items-center justify-between w-full px-4 py-3 text-lg sm:text-xl font-extrabold text-slate-100 rounded-xl hover:bg-white/5 hover:text-[#F1B528] transition-all"
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${dropdownOpen ? 'rotate-180 text-[#F1B528]' : 'text-slate-400'}`} />
                      </button>
                      {dropdownOpen && (
                        <div className="pl-4 border-l-2 border-[#F1B528]/60 space-y-1 mt-1 ml-4">
                          {item.children.map((child, childIdx) => (
                            <Link
                              key={childIdx}
                              to={child.href}
                              onClick={() => setIsOpen(false)}
                              className={`block px-4 py-2.5 text-sm sm:text-base font-semibold rounded-xl transition-colors hover:bg-white/5 hover:text-[#F1B528] ${
                                isActive(child.href) ? 'text-[#F1B528] bg-white/5' : 'text-slate-400'
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                const active = isActive(item.href);
                return (
                  <Link
                    key={index}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 text-lg sm:text-xl font-extrabold rounded-xl transition-all hover:bg-white/5 hover:text-[#F1B528] ${
                      active ? 'text-[#F1B528] bg-white/5' : 'text-slate-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Sidebar info inside the menu */}
            <div className="md:col-span-5 space-y-8 border-t md:border-t-0 md:border-l border-white/5 pt-8 md:pt-0 md:pl-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#D99C1B] mb-4">Kontakt Info</p>
                <div className="space-y-4">
                  <a href="tel:+4575695900" className="block group">
                    <span className="text-xs text-slate-400 block">Telefon</span>
                    <span className="text-base font-bold text-[#F1B528] group-hover:text-[#D99C1B] transition-colors">75 69 59 00</span>
                  </a>
                  <a href="mailto:bm@bmmaler.dk" className="block group">
                    <span className="text-xs text-slate-400 block">E-mail</span>
                    <span className="text-base font-bold text-[#F1B528] group-hover:text-[#D99C1B] transition-colors">bm@bmmaler.dk</span>
                  </a>
                  <div>
                    <span className="text-xs text-slate-400 block">Adresse</span>
                    <span className="text-sm font-semibold text-slate-300">Håndværkervej 3A, 7120 Vejle Øst</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/60 p-5 rounded-3xl border border-white/5">
                <h4 className="text-sm font-bold text-slate-200 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#F1B528]" />
                  Kvalitet & Erfaring
                </h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Vi går højt op i kvalitet, ordentlighed og høj faglighed. Vores uddannede malere sikrer et professionelt resultat hver gang.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
